// CameraMonitorPopup.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fetchCameraMonitor, fetchCameraMonitorFromInstance, buildCameraUrls } from '../services/cameraMonitorApi';
import './CSS/CameraMonitor.css';

const CameraMonitorPopup = () => {
  // All cams of the event (tabs). Falls back to just the one in `camera` when
  // the opener doesn't pass the `cams` param (old links keep working).
  const [cams, setCams] = useState([]);
  const [camera, setCamera] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [monitorData, setMonitorData] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [loading, setLoading] = useState(true);
  // Guards against a slow response from a previous tab landing on the current one
  const activePathRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cameraParam = params.get('camera');
    const baseUrlParam = params.get('baseUrl');
    const instanceIdParam = params.get('instanceId');
    const camsParam = params.get('cams');

    try {
      let initial = null;
      if (cameraParam) {
        initial = JSON.parse(decodeURIComponent(cameraParam));
        if (initial && instanceIdParam && !initial.instanceId) {
          initial.instanceId = instanceIdParam;
        }
      }

      let list = [];
      if (camsParam) {
        const parsed = JSON.parse(decodeURIComponent(camsParam));
        if (Array.isArray(parsed)) list = parsed.filter((c) => c && c.name && c.path);
      }
      if (initial && !list.some((c) => c.path === initial.path)) {
        list = [initial, ...list];
      }

      setCams(list);
      setCamera(initial || list[0] || null);
      if (baseUrlParam) setBaseUrl(decodeURIComponent(baseUrlParam));
    } catch (error) {
      console.error('Error parsing camera parameters:', error);
    }
  }, []);

  useEffect(() => {
    document.title = camera ? `Monitor · ${camera.name}` : 'Camera Monitor';
  }, [camera]);

  const fetchData = useCallback(async () => {
    if (!camera || !baseUrl) return;
    const path = camera.path;
    activePathRef.current = path;

    // Try instance proxy first (works before event starts)
    if (camera.instanceId) {
      const instanceResult = await fetchCameraMonitorFromInstance(camera.instanceId);
      if (activePathRef.current !== path) return; // user switched tab meanwhile
      if (instanceResult.success) {
        setMonitorData(instanceResult);
        setDataSource('instance');
        setLoading(false);
        return;
      }
    }

    // Fall back to CloudFront (works after event starts + sync-x)
    const result = await fetchCameraMonitor(baseUrl, path);
    if (activePathRef.current !== path) return;
    setMonitorData(result);
    setDataSource(result.success ? 'cloudfront' : null);
    setLoading(false);
  }, [camera, baseUrl]);

  useEffect(() => {
    fetchData();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchData();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefresh, refreshInterval]);

  const selectCam = (cam) => {
    if (!cam || cam.path === camera?.path) return;
    setCamera(cam);
    setMonitorData(null);
    setDataSource(null);
    setLoading(true);
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'good': return 'var(--mon-ok)';
      case 'warning': return 'var(--mon-warn)';
      case 'error': return 'var(--mon-err)';
      default: return 'var(--mon-muted)';
    }
  };

  const formatKbps = (kbps) => {
    if (kbps == null) return 'N/A';
    if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
    return `${kbps} kbps`;
  };

  const formatDuration = (sec) => {
    if (sec == null) return 'N/A';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const formatMB = (mb) => {
    if (mb == null || mb === 0) return 'N/A';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  // Mismos umbrales que calculate_health() en rtmp-monitor.py: 85% warn, 95% critical.
  const getDiskColor = (percent) => {
    if (percent == null) return 'var(--mon-muted)';
    if (percent >= 95) return 'var(--mon-err)';
    if (percent >= 85) return 'var(--mon-warn)';
    return 'var(--mon-ok)';
  };

  const DISK_LABELS = {
    root:     'Root (/)',
    segments: 'Segments (remote)',
  };

  // srs_hls: SRS HLS output is disabled since 2026-10-13 (it only fed the
  // removed proxy -> normalizer chain) — nothing writes there anymore, but the
  // node monitor may still report the mount, so hide it client-side.
  const HIDDEN_DISKS = new Set(['srs_hls']);

  if (!camera || !baseUrl) {
    return (
      <div className="camera-monitor popup-mode">
        <div className="loading-screen">
          <h2>Loading camera monitor...</h2>
        </div>
      </div>
    );
  }

  const urls = buildCameraUrls(baseUrl, camera.path);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="loading-screen">
          <h2>Loading monitor data...</h2>
        </div>
      );
    }

    if (!monitorData || !monitorData.success) {
      return (
        <div className="monitor-card error">
          <p className="error-message">Error: {monitorData?.error || 'Failed to load monitor data'}</p>
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <a href={urls.monitor} target="_blank" rel="noopener noreferrer" className="url-link">
              Try opening monitor JSON directly
            </a>
          </div>
        </div>
      );
    }

    const m = monitorData.data;
    const healthColor = getHealthColor(m.health?.status);

    return (
      <div className="monitor-card">
        <div className="monitor-info">
          {/* Health */}
          <div className="info-section">
            <h4>Health</h4>
            <div className="quality-status" style={{ borderColor: healthColor }}>
              <div className="quality-score" style={{ color: healthColor }}>
                {m.health?.score ?? 0}
              </div>
              <div className="quality-label" style={{ color: healthColor }}>
                {m.health?.status || 'unknown'}
              </div>
            </div>
            {m.health?.issues && m.health.issues.length > 0 && (
              <div className="quality-issues">
                <strong>Issues:</strong>
                <ul>
                  {m.health.issues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Ingest */}
          <div className="info-section">
            <h4>Ingest</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Active:</span>
                <span className={`value ${m.ingest?.active ? 'active' : 'inactive'}`}>
                  {m.ingest?.active ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Protocol:</span>
                <span className="value">{m.ingest?.protocol?.toUpperCase() || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Stream:</span>
                <span className="value">{m.ingest?.stream_name || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Source IP:</span>
                <span className="value">{m.ingest?.ip || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Bitrate:</span>
                <span className="value">{formatKbps(m.ingest?.kbps_in)}</span>
              </div>
              <div className="status-item">
                <span className="label">Resolution:</span>
                <span className="value">{m.ingest?.resolution || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Codec:</span>
                <span className="value">{m.ingest?.codec || 'N/A'}{m.ingest?.audio_codec ? ` / ${m.ingest.audio_codec}` : ''}</span>
              </div>
              <div className="status-item">
                <span className="label">Alive:</span>
                <span className="value">{formatDuration(m.ingest?.alive_sec)}</span>
              </div>
            </div>
          </div>

          {/* Slate and HLS Proxy sections removed: the slate/normalizer/failover
              proxy chain no longer exists post-IR-198 (no /proxy/status route,
              no slate stream in SRS) — both sections could only read empty. */}

          {/* Encoder */}
          <div className="info-section">
            <h4>Encoder</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Running:</span>
                <span className={`value ${m.encoder?.running ? 'active' : 'inactive'}`}>
                  {m.encoder?.running ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Speed:</span>
                <span className={`value ${m.encoder?.speed && m.encoder.speed >= 0.95 ? 'active' : (m.encoder?.speed ? 'inactive' : '')}`}>
                  {m.encoder?.speed != null ? `${m.encoder.speed}x` : 'N/A'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">FPS:</span>
                <span className="value">{m.encoder?.fps ?? 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">CPU:</span>
                <span className="value">{m.encoder?.cpu_percent != null ? `${m.encoder.cpu_percent.toFixed(1)}%` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Memory:</span>
                <span className="value">{m.encoder?.memory_mb != null ? `${m.encoder.memory_mb.toFixed(0)} MB` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Encoded Time:</span>
                <span className="value">{m.encoder?.encoded_time || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Segments */}
          <div className="info-section">
            <h4>DASH Segments</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Active:</span>
                <span className={`value ${m.segments?.active ? 'active' : 'inactive'}`}>
                  {m.segments?.active ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Count:</span>
                <span className="value">{m.segments?.segment_count ?? 0}</span>
              </div>
              <div className="status-item">
                <span className="label">Latest Age:</span>
                <span className="value">{m.segments?.latest_age_sec != null ? `${m.segments.latest_age_sec.toFixed(1)}s` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Rate:</span>
                <span className="value">{m.segments?.segments_per_min != null ? `${m.segments.segments_per_min.toFixed(1)}/min` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Last Segment:</span>
                {/* tracked_* comes from the encoder's own log (instant, PUT-safe);
                    last_segment_number is readdir-based and can lag or be blind. */}
                <span className="value">
                  {(m.drift?.tracked_segment_number ?? m.drift?.last_segment_number) != null
                    ? `#${m.drift?.tracked_segment_number ?? m.drift?.last_segment_number}`
                    : 'N/A'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Total Size:</span>
                <span className="value">{formatMB(m.segments?.total_size_mb)}</span>
              </div>
            </div>
          </div>

          {/* Disk */}
          {m.disk && (
            <div className="info-section">
              <h4>Disk</h4>
              <table className="monitor-table">
                <thead>
                  <tr>
                    <th>Mount</th>
                    <th className="num">Free</th>
                    <th className="num">Total</th>
                    <th className="num">Used</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(m.disk).filter(([key]) => !HIDDEN_DISKS.has(key)).map(([key, d]) => (
                    <tr key={key}>
                      <td>
                        {DISK_LABELS[key] || key}
                        <span className="dim">{d.path}</span>
                      </td>
                      {d.available ? (
                        <>
                          <td className="num">{d.free_gb} GB</td>
                          <td className="num">{d.total_gb} GB</td>
                          <td className="num" style={{ color: getDiskColor(d.percent_used), fontWeight: 'bold' }}>
                            {d.percent_used}%
                          </td>
                        </>
                      ) : (
                        /* En 'segments' esto suele significar que el mount SSHFS ha caido */
                        <td colSpan={3} className="num" style={{ color: 'var(--mon-err)' }}>
                          unreadable
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* SRS Streams */}
          {m.srs?.streams && m.srs.streams.length > 0 && (
            <div className="info-section">
              <h4>SRS Streams {m.srs?.connected ? '' : '(disconnected)'}</h4>
              <table className="monitor-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Proto</th>
                    <th>IP</th>
                    <th className="num">Bitrate</th>
                    <th>Video</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {m.srs.streams.map((s, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: s.is_camera ? 'bold' : 'normal' }}>{s.app}/{s.name}</td>
                      <td>{s.protocol?.toUpperCase()}</td>
                      <td>{s.ip}</td>
                      <td className="num">{formatKbps(s.kbps_in)}</td>
                      <td>{s.video ? `${s.video.codec} ${s.video.width}x${s.video.height}` : 'N/A'}</td>
                      {/* is_camera excluye slate Y normalized, asi que 'no camera'
                          ya no implica slate: hay que mirar is_slate. */}
                      <td style={{ color: s.is_camera ? 'var(--mon-ok)' : (s.is_slate ? 'var(--mon-warn)' : 'var(--mon-muted)') }}>
                        {s.is_camera ? 'Camera' : (s.is_slate ? 'Slate' : 'Internal')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Drift section removed: cross-camera alignment lives in the Studio's
              Camera sync panel (/monitor) since IR-198; the only field worth
              keeping (last produced segment) moved into DASH Segments above. */}

          {/* Timestamp & URLs */}
          <div className="info-section">
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Last Update:</span>
                <span className="value">
                  {m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="info-section urls">
            <h4>Stream URLs</h4>
            <div className="url-item">
              <span className="label">MPD:</span>
              <a href={urls.mpd} target="_blank" rel="noopener noreferrer" className="url-link">
                {urls.mpd}
              </a>
            </div>
            <div className="url-item">
              <span className="label">Monitor JSON:</span>
              <a href={urls.monitor} target="_blank" rel="noopener noreferrer" className="url-link">
                {urls.monitor}
              </a>
            </div>
            <div className="url-item">
              <span className="label">History JSON:</span>
              <a href={urls.history} target="_blank" rel="noopener noreferrer" className="url-link">
                {urls.history}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="camera-monitor popup-mode">
      <div className="monitor-header">
        <div className="monitor-title">
          <h2>Camera Monitor</h2>
          {dataSource && (
            <span className={`source-tag ${dataSource}`}>
              {dataSource === 'instance' ? 'Instance' : 'CloudFront'}
            </span>
          )}
        </div>
        <div className="controls">
          <label>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh
          </label>
          {autoRefresh && (
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
            >
              <option value={2000}>2s</option>
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
            </select>
          )}
          <button onClick={fetchData} className="refresh-btn">
            Refresh
          </button>
          <button onClick={() => window.close()} className="close-btn">
            Close
          </button>
        </div>
      </div>

      {cams.length > 0 && (
        <div className="monitor-tabs">
          {cams.map((c) => (
            <button
              key={c.path}
              className={`monitor-tab ${c.path === camera.path ? 'active' : ''}`}
              onClick={() => selectCam(c)}
              title={c.path}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {renderBody()}
    </div>
  );
};

export default CameraMonitorPopup;
